import React, { useState, type FC } from 'react';
import { NavLink } from 'react-router-dom';
import {
    Activity,
    LayoutGrid,
    FileText,
    LifeBuoy,
    GraduationCap,
    Settings,
    MoreHorizontal,
    X,
    Zap
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useNotificationBadge } from '../../features/layouts/hooks/useNotificationBadge';

// =====================================================
// COMPONENT
// =====================================================

interface TabItem {
    path?: string;
    label: string;
    icon: React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }>;
    isMenu?: boolean;
    subItems?: Array<{ label: string; icon: React.ComponentType<{ size?: number; className?: string }>; path: string }>;
    badge?: number;
}

const BottomTabBar: FC<{ isAdmin?: boolean; isFranchise?: boolean; isRider?: boolean }> = ({ isAdmin, isFranchise, isRider }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { unreadCount } = useNotificationBadge();

    const adminTabs: TabItem[] = [
        { path: '/dashboard', label: 'Finanzas', icon: Activity },
        { path: '/admin/flyder', label: 'Flyder', icon: Zap },
        { path: '/admin/resources', label: 'Documentos', icon: FileText },
        { path: 'menu', label: 'Más', icon: MoreHorizontal, isMenu: true, subItems: [
            { label: 'Ayuda', icon: LifeBuoy, path: '/admin/support' },
            { label: 'Academia', icon: GraduationCap, path: '/admin/academy' },
            { label: 'Tareas', icon: LayoutGrid, path: '/admin/kanban' },
        ]},
        { path: '/profile', label: 'Ajustes', icon: Settings },
    ];

    const franchiseTabs: TabItem[] = [
        { path: '/dashboard', label: 'Finanzas', icon: Activity },
        { path: '/flyder', label: 'Flyder', icon: Zap },
        { path: '/operations', label: 'Turnos', icon: LayoutGrid },
        { path: 'menu', label: 'Más', icon: MoreHorizontal, isMenu: true, subItems: [
            { label: 'Documentos', icon: FileText, path: '/resources' },
            { label: 'Ayuda', icon: LifeBuoy, path: '/support' },
            { label: 'Academia', icon: GraduationCap, path: '/academy' },
        ]},
        { path: '/profile', label: 'Ajustes', icon: Settings },
    ];

    const riderTabs: TabItem[] = [
        { path: '/rider/profile', label: 'Perfil', icon: Activity },
        { path: '/rider/profile/notifications', label: 'Notificaciones', icon: MoreHorizontal, badge: unreadCount },
        { path: '/rider/profile/security', label: 'Seguridad', icon: Settings },
    ];

    const tabs = isAdmin ? adminTabs : isFranchise ? franchiseTabs : isRider ? riderTabs : [];

    return (
        <>
            <nav className="xl:hidden tab-dock" aria-label="Navegación principal">
                <div className="relative flex items-stretch justify-between px-1">
                    {tabs.map((tab) => {
                        const Icon = tab.icon;

                        if (tab.isMenu && tab.subItems) {
                            return (
                                <div key="menu-container" className="relative flex flex-1 items-stretch justify-center">
                                    <button
                                        className={cn(
                                            'flex min-h-16 w-full flex-col items-center justify-center gap-1 rounded-lg px-1 text-slate-500 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ruby-500 dark:text-slate-400',
                                            isMenuOpen && 'bg-ruby-50 text-ruby-700 dark:bg-ruby-500/15 dark:text-ruby-300'
                                        )}
                                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                                        aria-expanded={isMenuOpen}
                                        aria-controls="mobile-more-menu"
                                    >
                                        {isMenuOpen ? <X size={22} strokeWidth={2.25} aria-hidden="true" /> : <Icon size={22} strokeWidth={2} aria-hidden="true" />}
                                        <span className="max-w-full truncate text-[11px] font-semibold leading-none">{tab.label}</span>
                                    </button>

                                    {isMenuOpen && (
                                        <div id="mobile-more-menu" className="app-chrome absolute bottom-full left-1/2 mb-3 w-56 -translate-x-1/2 overflow-hidden p-2">
                                            <div className="space-y-1">
                                                <div className="px-2 py-1.5">
                                                    <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                                                        Secciones
                                                    </p>
                                                </div>
                                                {tab.subItems.map((subItem) => {
                                                    const SubIcon = subItem.icon;
                                                    return (
                                                        <NavLink
                                                            key={subItem.path}
                                                            to={subItem.path}
                                                            onClick={() => setIsMenuOpen(false)}
                                                            className={({ isActive: subIsActive }) => cn(
                                                                'flex min-h-11 items-center gap-3 rounded-lg px-3 py-2 text-sm font-semibold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ruby-500',
                                                                subIsActive ? 'bg-ruby-50 text-ruby-700 dark:bg-ruby-500/15 dark:text-ruby-300' : 'text-slate-700 hover:bg-black/[0.04] dark:text-slate-300 dark:hover:bg-white/[0.07]'
                                                            )}
                                                        >
                                                            {({ isActive: subIsActive }) => (
                                                                <>
                                                                    <SubIcon size={19} className={cn('shrink-0', subIsActive ? 'text-ruby-600 dark:text-ruby-400' : 'text-slate-500 dark:text-slate-400')} aria-hidden="true" />
                                                                    <span>{subItem.label}</span>
                                                                </>
                                                            )}
                                                        </NavLink>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        }

                        return (
                            <NavLink
                                key={tab.path || 'tab'}
                                to={tab.path || '#'}
                                className={({ isActive }) => cn(
                                    'tab-item min-h-16 flex-1 rounded-lg px-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ruby-500',
                                    isActive ? 'bg-ruby-50 text-ruby-700 dark:bg-ruby-500/15 dark:text-ruby-300' : 'text-slate-500 dark:text-slate-400'
                                )}
                            >
                                {({ isActive }) => (
                                    <div className="flex h-full flex-col items-center justify-center gap-1 py-2">
                                        <Icon size={22} strokeWidth={isActive ? 2.4 : 2} aria-hidden="true" />
                                        <span className="max-w-full truncate text-[11px] font-semibold leading-none">{tab.label}</span>
                                    </div>
                                )}
                            </NavLink>
                        );
                    })}
                </div>
            </nav>

            {/* Overlay for menu */}
            {isMenuOpen && (
                <div
                    className="fixed inset-0 z-40 bg-slate-950/35 backdrop-blur-[2px] xl:hidden"
                    onClick={() => setIsMenuOpen(false)}
                    aria-hidden="true"
                />
            )}
        </>
    );
};

export default React.memo(BottomTabBar);
